<template>
  <div>
    <div>
      <consultations-search-bar
        v-if="isExaminationsFiltersLoaded"
        :search-fields="searchFields"
        :search-options="searchOptions"
        :products="products"
        :selected-extra-case-filters-list="selectedExtraCaseFiltersList"
        :is-searching="isSearching"
        :is-advanced-search-open="isAdvancedSearchOpen"
        :options-loading="searchOptionsLoading"
        :mole-diagnosis-fields-visible="moleDiagnosisFieldsVisible"
        :is-mole-uk-product="isMoleUkProduct"
        :result-total="paginate.total"
        @search="getResults(1)"
        @clear-search="clearSearch"
        @select-main-filter="filter($event)"
        @select-extra-filters="selectExtraFilters($event)"
        @select-product="selectProduct($event)"
        @toggle-advanced-search="toggleAdvancedSearch"
      ></consultations-search-bar>

      <div id="evaluations">
        <clip-loader v-show="loading"></clip-loader>

        <consultations-table
          :examinations="examinations"
          :sort-by-selection="sortBySelection"
          :user-has-no-products="userHasNoProducts"
          :user-has-no-products-message="userHasNoProductsMessage"
          @sort="sortBy"
          @open="openExamination"
        ></consultations-table>

        <div class="row-fluid float-right">
          <DerPaginate
            v-model="paginate.current_page"
            :page-count="paginate.last_page"
            @change="getResults"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ConsultationsSearchBar from "./ConsultationsSearchBar.vue";
import ConsultationsTable from "./ConsultationsTable.vue";

import {
  getProductType,
  PRODUCT_ID,
  productHasPermission,
} from "../../helpers/product";
import { PRODUCT_PERMISSIONS, PRODUCTS } from "../../helpers/definitions";
import DerPaginate from "../../design-system/DerPaginate.vue";
import { setHistoryState } from "../../lib/history-state";
import { createWorklistFetcher, withCounts } from "../../lib/worklist-fetch";
import { practitionerHttpService } from "../../app/practitioner-portal/store/practitioner-http-service";

export default {
  components: {
    ConsultationsSearchBar,
    ConsultationsTable,
    DerPaginate,
  },
  data() {
    return {
      loading: true,
      hasMultipleFeatures: false,
      isAdvancedSearchOpen: false,
      products: this.filterProducts(),
      user: this.$store.state.user,
      fetcher: createWorklistFetcher({
        endpoint: "/api/internal/examinations",
        countEndpoint: "/api/internal/count-examinations",
        tabFields: ["mainFilter"],
      }),
      searchOptionsLoading: false,
      optionsLoaded: false,
      examinationCounts: null,
      baseExaminationsFilters: null,
      searchOptions: {
        centers: [],
        users: [],
        userTypes: [],
        products: [],
        statuses: [],
        examinationsFilters: [],
        extraFilters: [],
      },
      selectedExtraFilters: [],
      selectedExtraCaseFiltersList: [],
      isSearching: false,
      paginate: {
        current_page: 0,
        total: 0,
        last_page: 0,
      },
      searchFields: {
        query: "",
        dateFrom: "",
        dateTo: "",
        tags: "",
        center: [],
        plannedDiagnosisId: "",
        dermatoscopicDiagnosisId: "",
        histopathologicalDiagnosisId: "",
        user: "",
        userType: null,
        products: null,
        status: null,
        mainFilter: null,
        sort: {
          field: "createdAt",
          orderBy: "DESC",
        },
        flagged: "",
        prioritized: "",
        bookmarked: "",
        unreadMessages: "",
        unreadPrivateMessages: "",
        aiPrioritized: "",
        page: 1,
      },
      examinations: [],
      sortBySelection: "createdAt",
      userHasNoProducts: false,
      userHasNoProductsMessage: this.trans(
        "errorMessages.userHasNoActiveProducts",
      ),
      moleDiagnosisFieldsVisible: false,
      productsWithPermission: {},
      PRODUCT_ID: PRODUCT_ID,
    };
  },
  computed: {
    userSingleProductId: function () {
      if (this.products) {
        if (this.products.length === 1) {
          return this.products[0].id;
        }
        return "";
      }
    },
    examinationNavigationDetails() {
      return {
        examinations: this.examinations.map((examination) => ({
          id: examination.id,
          editUrl: examination.editUrl,
        })),
        paginationInfo: { ...this.paginate },
      };
    },
    isMoleUkProduct() {
      return (
        this.searchFields.products == PRODUCT_ID.MOLE_UK ||
        this.userSingleProductId == PRODUCT_ID.MOLE_UK
      );
    },
    isExaminationsFiltersLoaded() {
      return (
        this.searchOptions.examinationsFilters &&
        this.searchOptions.examinationsFilters.length > 0
      );
    },
  },
  watch: {
    "searchFields.products": function () {
      // trigger check on filter selection
      if (this.searchFields.products) {
        this.productHasDiagnosisSearchFields(this.searchFields.products);
      }
    },
  },
  created: function () {
    this.setQueryFields(true);
    this.getResults();
    this.setProductSpecificSearchFields();
  },
  methods: {
    decorateExaminationsFilters: function () {
      const decorated = withCounts(
        this.baseExaminationsFilters,
        this.examinationCounts,
        {
          "": this.examinationCounts?.allExaminations,
          new_no_consensus: this.examinationCounts?.newNoConsensusExaminations,
          all_ongoing: this.examinationCounts?.allOngoingExaminations,
          my_ongoing: this.examinationCounts?.myOngoingExaminations,
          old_cases_3_days: this.examinationCounts?.oldExaminations3Days,
          old_cases: this.examinationCounts?.oldExaminations,
          with_requested_but_not_signed_study_consents:
            this.examinationCounts?.withRequestedButNotSignedStudyConsents,
          with_signed_study_consents:
            this.examinationCounts?.withSignedStudyConsents,
        },
      );

      if (decorated) {
        this.searchOptions.examinationsFilters = decorated;
      }
    },
    getResults: function (page) {
      this.loading = true;

      this.searchFields.page = page ? page : this.searchFields.page;

      localStorage.setItem("last_query_uri", JSON.stringify(this.searchFields));

      this.fetchRows();
      this.fetchCounts();
      this.fetchOptions();
    },
    fetchRows: function () {
      this.fetcher
        .rows(this.searchFields)
        .then((collection) => {
          if (!collection) {
            return;
          }
          if (collection.error) {
            this.userHasNoProducts = true;
            return;
          }

          this.examinations = collection.data;
          this.hasMultipleFeatures = collection.hasMultipleFeatures;
          this.paginate = collection.paginate;
          this.searchOptions = {
            ...this.searchOptions,
            ...collection.searchOptions,
          };
          this.baseExaminationsFilters =
            collection.searchOptions.examinationsFilters;
          this.decorateExaminationsFilters();

          if (!this.hasMultipleFeatures && this.products.length !== 0) {
            this.searchFields.products = this.products[0].id;
          }

          this.setQueryFields();
        })
        .catch((error) => this.userErrorMessage(error))
        .finally(() => {
          this.loading = false;
        });
    },
    fetchCounts: function () {
      this.fetcher
        .counts(this.searchFields)
        .then((counts) => {
          // null means the filters have not changed, so the numbers still hold.
          if (counts && !counts.error) {
            this.examinationCounts = counts;
            this.decorateExaminationsFilters();
          }
        })
        .catch((error) => this.userErrorMessage(error));
    },
    fetchOptions: function () {
      if (this.optionsLoaded) {
        return;
      }

      this.searchOptionsLoading = true;
      this.fetcher
        .options()
        .then((options) => {
          if (options.error) {
            return;
          }

          this.optionsLoaded = true;
          this.searchOptions = {
            ...this.searchOptions,
            ...options.searchOptions,
          };
        })
        .catch((error) => this.userErrorMessage(error))
        .finally(() => {
          this.searchOptionsLoading = false;
        });
    },
    selectExtraFilters: function (filtersList) {
      const oldSelectedExtraFilters = this.selectedExtraFilters;

      this.selectedExtraFilters = filtersList;

      const newSelectedExtraFilters = this.selectedExtraFilters;

      this.selectedExtraCaseFiltersList = filtersList;

      oldSelectedExtraFilters.forEach((el) => {
        if (
          newSelectedExtraFilters.length !== 0 &&
          newSelectedExtraFilters.includes(el.value)
        ) {
          return;
        } else {
          // if element is not in the new list, assign ""
          this.searchFields[el.value] = "";
        }
      });

      // assign 1 to all elements in the new list
      newSelectedExtraFilters.forEach((element) => {
        this.searchFields[element.value] = 1;
      });

      this.$notify({
        text: this.trans("general.notificationSaved"),
        type: "success",
        duration: 1000,
      });

      this.getResults(1);
    },
    selectProduct: function (product) {
      this.searchFields.products = product;

      // these props either don't exist on all or differ between products
      this.searchFields.plannedDiagnosisId = "";
      this.searchFields.dermatoscopicDiagnosisId = "";
      this.searchFields.histopathologicalDiagnosisId = "";
      this.searchFields.histopathologicalStatusId = "";
      this.searchFields.differentialDiagnosisId = "";
      this.searchFields.tags = "";
    },
    sortBy: function (field, event) {
      this.loading = true;
      this.sortBySelection = field;

      // Reset all to bottom
      if (field !== this.searchFields.sort.field) {
        $("i.fas.fa-caret-up")
          .removeClass("fa-caret-up")
          .addClass("fa-caret-down");

        this.searchFields.sort.orderBy = "DESC";
      }

      this.searchFields.sort.field = field;

      if (this.searchFields.sort.orderBy == "ASC") {
        this.searchFields.sort.orderBy = "DESC";
        $(event.currentTarget)
          .find("i.fas.fa-caret-up")
          .removeClass("fa-caret-up")
          .addClass("fa-caret-down");
      } else {
        this.searchFields.sort.orderBy = "ASC";
        $(event.currentTarget)
          .find("i.fas.fa-caret-down")
          .removeClass("fa-caret-down")
          .addClass("fa-caret-up");
      }

      localStorage.setItem("last_query_uri", JSON.stringify(this.searchFields));

      this.getResults(this.currentPage);
    },
    resetAllSortBy: function () {
      $("th.sort-by")
        .find("i.fas.fa-caret-up")
        .removeClass("fa-caret-up")
        .addClass("fa-caret-down");
      this.searchFields.sort.orderBy = "DESC";
    },
    clearSearch: function () {
      this.isSearching = false;
      this.isAdvancedSearchOpen = false;
      this.clearQuerySearch();
      this.getResults(1);
    },
    clearQuerySearch: function () {
      localStorage.removeItem("last_query_uri");
      localStorage.removeItem("last_visited_examination_id");

      // The case-filter selections survive a clear; everything else resets.
      const keptExtraFilters = Object.fromEntries(
        [
          "flagged",
          "prioritized",
          "bookmarked",
          "unreadMessages",
          "unreadPrivateMessages",
          "aiPrioritized",
          "excludeTestPatients",
          "privateMessages",
          "aiPriorityLow",
          "aiPriorityMedium",
          "aiPriorityHigh",
        ].map((field) => [field, this.searchFields[field] || ""]),
      );

      this.searchFields = {
        query: "",
        dateFrom: "",
        dateTo: "",
        tags: "",
        center: [],
        plannedDiagnosisId: "",
        dermatoscopicDiagnosisId: "",
        histopathologicalDiagnosisId: "",
        histopathologicalStatusId: "",
        differentialDiagnosisId: "",
        user: "",
        products: null,
        status: null,
        mainFilter: this.searchFields.mainFilter,
        sort: {
          field: "createdAt",
          orderBy: "DESC",
        },
        ...keptExtraFilters,
        page: 1,
      };

      this.resetAllSortBy();
      $('select[name="center"]').val("").trigger("change");
      $('select[name="users"]').val("").trigger("change");
      $('select[name="status"]').val("").trigger("change");
      $('select[name="products"]').val("").trigger("change");
    },
    filter: function (filterStatus) {
      this.resetAllSortBy();

      this.searchFields.sort.field = "createdAt";
      this.searchFields.mainFilter = filterStatus;

      localStorage.setItem("last_query_uri", JSON.stringify(this.searchFields));

      this.$notify({
        text: this.trans("general.notificationSaved"),
        type: "success",
        duration: 1000,
      });

      this.getResults(1);
    },
    openExamination: function (examination) {
      this.$router.push({
        path: "/consultations" + examination.editUrl,
        state: setHistoryState(
          "examinationNavigationDetails",
          this.examinationNavigationDetails,
        ),
      });
    },
    toggleAdvancedSearch: function () {
      if (this.isAdvancedSearchOpen) {
        this.isAdvancedSearchOpen = false;
        this.clearSearch();
      } else {
        this.isAdvancedSearchOpen = true;
        this.clearQuerySearch();
      }
    },
    setQueryFields: function (initializeFromLocalstorage) {
      const queryUri = localStorage.getItem("last_query_uri");
      this.isSearching = false;

      if (queryUri) {
        const parsedQueryUri = JSON.parse(queryUri);

        if (initializeFromLocalstorage) {
          this.searchFields = parsedQueryUri;
        }

        // assign extra filter values after page refresh
        let list = [];

        for (let key in this.searchFields) {
          this.searchOptions.extraFilters.find((e) => {
            if (e.value === key && this.searchFields[key] == 1) {
              list.push(e);
            }
          });
        }

        this.selectedExtraCaseFiltersList = list;
        this.selectedExtraFilters = list;

        if (this.searchFields.query.length > 0 && !this.isAdvancedSearchOpen) {
          this.isSearching = true;
        }

        // if advanced search filter is applied - advanced search is open and user can remove selection
        for (var key in this.searchFields) {
          let value = this.searchFields[key];
          const hasValue = Array.isArray(value) ? value.length > 0 : !!value;

          if (
            hasValue &&
            !inArray(key, [
              "sort",
              "query",
              "page",
              "mainFilter",
              "flagged",
              "prioritized",
              "bookmarked",
              "unreadMessages",
              "unreadPrivateMessages",
              "aiPrioritized",
              "excludeTestPatients",
              "privateMessages",
              "aiPriorityLow",
              "aiPriorityMedium",
              "aiPriorityHigh",
              "products",
            ])
          ) {
            this.isAdvancedSearchOpen = true;
            this.isSearching = true;
          }
        }
      }
    },
    filterProducts: function () {
      return this.$store.state.user.products;
    },
    productHasDiagnosisSearchFieldsPermission: async function (productId) {
      if (!this.productsWithPermission[productId]) {
        const product = await practitionerHttpService
          .get(`/api/internal/products/${productId}`)
          .then((data) => {
            return data.data;
          })
          .catch(() => {});

        if (product) {
          Object.assign(this.productsWithPermission, {
            [product.meta.id]: product,
          });

          return productHasPermission(
            PRODUCT_PERMISSIONS.USE_DIAGNOSIS_SE,
            product,
          );
        }
      } else {
        return productHasPermission(
          PRODUCT_PERMISSIONS.USE_DIAGNOSIS_SE,
          this.productsWithPermission[productId],
        );
      }
    },
    productHasDiagnosisSearchFields: async function (productId) {
      const productHasPermission =
        await this.productHasDiagnosisSearchFieldsPermission(productId);

      if (this.$can("view_mole_se_diagnosis") && productHasPermission) {
        return (this.moleDiagnosisFieldsVisible = true);
      }
      return (this.moleDiagnosisFieldsVisible = false);
    },
    setProductSpecificSearchFields: function () {
      // if user has only mole / skåne mole, they should see specific search fields straight away
      const moleProductIds = [
        getProductType(PRODUCTS.MOLE),
        getProductType(PRODUCTS.REGION_SKANE_MOLE),
      ];

      const singleProductId = moleProductIds.find((id) => {
        return this.userSingleProductId === id;
      });

      if (singleProductId)
        this.productHasDiagnosisSearchFields(singleProductId);
    },
  },
};
</script>

<style lang="scss" scoped>
.filter-search-bar,
.advanced-search {
  background-color: #f5f5f5;
}

.advanced-search {
  padding-top: 0px;
}
</style>
